import { getCurrentTraceId } from "@/lib/monitoring/sentry";
import { getBrowserEnvironment } from "@/lib/utils/environment";
import { Error as AppError, ErrorDebugContext } from "@/util-components/root/types";
import { isValidElement } from "react";

const DEFAULT_TITLE = "Ein Fehler ist aufgetreten";
const DEFAULT_MESSAGE = "Ein unerwarteter Fehler ist aufgetreten.";

const hasWindow = typeof window !== "undefined";

const createLocation = (location?: Partial<AppError["location"]>): AppError["location"] => ({
  path: location?.path ?? "",
  redirect: location?.redirect ?? false,
});

export const buildClientDebugContext = (stackTrace?: string): ErrorDebugContext => ({
  request: {
    method: "CLIENT_SIDE",
    url: hasWindow ? window.location.href : "",
    headers: {},
  },
  response: {
    status: 0,
    statusText: "Client Side Exception",
    body: null,
  },
  environment: hasWindow ? getBrowserEnvironment() : undefined,
  stackTrace,
  timestamp: new Date().toISOString(),
});

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isDebugContext = (value: unknown): value is ErrorDebugContext =>
  isRecord(value) &&
  isRecord(value.request) &&
  isRecord(value.response) &&
  typeof value.timestamp === "string";

export const isAppError = (value: unknown): value is AppError =>
  isRecord(value) &&
  typeof value.message === "string" &&
  typeof value.statusCode === "number" &&
  "location" in value;

export const toPlainText = (value: unknown): string | undefined => {
  if (typeof value === "string") {
    return value;
  }

  if (typeof value === "number") {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value.map((entry) => toPlainText(entry)).filter(Boolean).join(", ") || undefined;
  }

  if (isValidElement(value)) {
    return undefined;
  }

  return undefined;
};

const normalizeRecordError = (
  value: Record<string, unknown>,
  fallback: Partial<Pick<AppError, "title" | "message" | "traceId" | "correlationId" | "debugContext">>,
): AppError => {
  const message =
    toPlainText(value.message) ??
    fallback.message ??
    toPlainText(value.error_description) ??
    DEFAULT_MESSAGE;
  const title = toPlainText(value.title) ?? fallback.title ?? DEFAULT_TITLE;
  const stackTrace = toPlainText(value.stack);

  return {
    message,
    title,
    error: toPlainText(value.error) ?? toPlainText(value.name) ?? "UnknownError",
    statusCode: typeof value.statusCode === "number" ? value.statusCode : 0,
    entity: value.entity as AppError["entity"],
    location: createLocation(value.location as Partial<AppError["location"]> | undefined),
    isClosable: typeof value.isClosable === "boolean" ? value.isClosable : true,
    withReload: typeof value.withReload === "boolean" ? value.withReload : false,
    mail: toPlainText(value.mail),
    showToast: typeof value.showToast === "boolean" ? value.showToast : undefined,
    traceId: toPlainText(value.traceId) ?? fallback.traceId ?? getCurrentTraceId(),
    correlationId: toPlainText(value.correlationId) ?? fallback.correlationId,
    debugContext: isDebugContext(value.debugContext)
      ? value.debugContext
      : fallback.debugContext ?? buildClientDebugContext(stackTrace),
  };
};

export const normalizeAppError = (
  input: unknown,
  fallback: Partial<Pick<AppError, "title" | "message" | "traceId" | "correlationId" | "debugContext">> = {},
): AppError => {
  if (isAppError(input)) {
    return {
      ...input,
      title: input.title || fallback.title || DEFAULT_TITLE,
      message: input.message || fallback.message || DEFAULT_MESSAGE,
      error: input.error || "UnknownError",
      statusCode: input.statusCode ?? 0,
      location: createLocation(input.location),
      isClosable: input.isClosable ?? true,
      withReload: input.withReload ?? false,
      traceId: input.traceId ?? fallback.traceId ?? getCurrentTraceId(),
      correlationId: input.correlationId ?? fallback.correlationId,
      debugContext: input.debugContext ?? fallback.debugContext ?? buildClientDebugContext(),
    };
  }

  if (input instanceof Error) {
    return {
      message: fallback.message ?? input.message ?? DEFAULT_MESSAGE,
      title: fallback.title ?? DEFAULT_TITLE,
      error: input.name || "Error",
      statusCode: 0,
      location: createLocation(),
      isClosable: true,
      withReload: false,
      traceId: fallback.traceId ?? getCurrentTraceId(),
      correlationId: fallback.correlationId,
      debugContext: fallback.debugContext ?? buildClientDebugContext(input.stack),
    };
  }

  if (isRecord(input)) {
    return normalizeRecordError(input, fallback);
  }

  return {
    message: fallback.message ?? DEFAULT_MESSAGE,
    title: fallback.title ?? DEFAULT_TITLE,
    error: "UnknownError",
    statusCode: 0,
    location: createLocation(),
    isClosable: true,
    withReload: false,
    traceId: fallback.traceId ?? getCurrentTraceId(),
    correlationId: fallback.correlationId,
    debugContext: fallback.debugContext ?? buildClientDebugContext(),
  };
};
